// Typed client for the Go API. Server components call with the absolute base
// URL; client components use the same-origin proxy via NEXT_PUBLIC_API_BASE.
// Types here mirror openapi/openapi.yaml — Phase 1 generates packages/api-types
// from the spec and imports them instead.

import { normalizeImage } from './image';

// Server components reach the Go backend directly; client components use the
// same-origin '/api' proxy (next.config rewrites) so it works on any host.
const BASE =
  typeof window === 'undefined'
    ? process.env.API_BASE ?? 'http://localhost:8080'
    : '';

export type AssetClass = 'TW_STOCK' | 'US_STOCK' | 'METAL' | 'CRYPTO';
export type Horizon = 'short' | 'medium' | 'long';
export type Recommendation = 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';

export interface Analysis {
  recommendation: Recommendation;
  confidence: number;
  target_price: number;
  support: number;
  resistance: number;
  summary: string;
  rationale: string;
  price_commentary: string;
  risks: string;
}

export interface AnalysisResp {
  symbol: string;
  horizon: Horizon;
  model: string;
  analysis: Analysis;
  disclaimer: string;
}

export interface NewsItem {
  id: string;
  rank: number;
  headline: string;
  summary: string;
  source_url: string;
  source_name: string;
  image_url: string;
}

export interface NewsResponse {
  date: string;
  locale: string;
  international: NewsItem[];
  taiwan: NewsItem[];
  notice: string;
}

export interface MarketQuote {
  symbol: string;
  name: string;
  asset_class?: AssetClass; // present ⇒ /stocks/[symbol] detail page exists
  price: string;
  change_pct: number;
  currency: string;
  ok: boolean;
}

export interface MarketQuoteGroup {
  key: string;
  name: string;
  quotes: MarketQuote[];
}

export interface MarketQuotesResponse {
  as_of: string;
  groups: MarketQuoteGroup[];
}

export interface ColumnCard {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  cover_image_url: string;
  source: 'human' | 'ai_generated';
  asset_class: AssetClass | null;
  author_name: string;
  author_kind: 'human' | 'ai_agent';
  published_at: string | null;
  locked: boolean;
  lock_reason?: 'quota' | 'category';
}

export interface ColumnDetail extends ColumnCard {
  body_md: string;
  author_bio: string;
  author_avatar_url: string;
}

export interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ToolCall {
  name: string;
  input: Record<string, unknown>;
}

export interface AgentResult {
  reply: string;
  tool_calls: ToolCall[] | null;
}

export interface Quote {
  symbol: string;
  price: string;
  currency: string;
  source: string;
  ts: string;
  notice: string;
}

export interface Candle {
  date: string;
  t: number; // epoch seconds (UTC)
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface StockDetail {
  symbol: string;
  asset_class: AssetClass;
  name: string;
  candles: Candle[];
  indicators: { ma5: number; ma20: number; ma60: number; rsi14: number; last: number };
  news: { headline: string; summary: string; source: string; url: string }[];
  ai: string;
  disclaimer: string;
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  locale: string;
  role: string;
  is_owner?: boolean;
}

export interface AuthResp {
  access_token: string;
  user: AuthUser;
}

export interface Usage {
  plan: string;
  daily_questions: number;
  sharp: boolean;
  used: Record<string, number>;
  remaining: Record<string, number>;
  monthly_columns: number; // -1 = unlimited
  columns_used: number;
  columns_remaining: number; // -1 = unlimited
  column_categories: string[] | null; // custom category grants (null = plan-based)
  plan_end: string | null; // paid-plan expiry
  ent_expires: string | null; // custom-permission expiry
}

// ApiError carries the HTTP status + the server's message code so the UI can
// branch on auth (401), paywall (402), and quota (429) without string-matching.
export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// One in-flight refresh shared by all concurrent 401s, so a burst of expired
// requests triggers a single token rotation instead of racing (rotation
// revokes the used refresh token — parallel refreshes would kill the session).
let refreshing: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function req<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include',
  });
  if (!res.ok) {
    // The 15-min access token expires mid-session: transparently rotate via
    // the refresh cookie and retry once. Client only (server components pass
    // cookies explicitly), and never for auth endpoints themselves.
    if (
      res.status === 401 &&
      !retried &&
      typeof window !== 'undefined' &&
      !path.startsWith('/api/v1/auth/')
    ) {
      if (await refreshSession()) return req<T>(path, init, true);
    }
    let code = res.statusText;
    try {
      const j = await res.json();
      if (j && typeof j.message === 'string') code = j.message;
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Short-lived shared cache for hot profile reads (me / usage). A fresh value
// serves for CACHE_TTL; concurrent callers share one in-flight request.
const CACHE_TTL = 15_000;
type Entry = { at: number; p: Promise<unknown> };
const profileCache = new Map<string, Entry>();

function cached<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const hit = profileCache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL) return hit.p as Promise<T>;
  const p = fn().catch((e) => {
    profileCache.delete(key); // don't cache failures
    throw e;
  });
  profileCache.set(key, { at: Date.now(), p });
  return p;
}

// Call after anything that changes plan/quota so the next read is fresh.
export function invalidateProfile() {
  profileCache.clear();
}

export const api = {
  health: () => req<{ status: string }>('/healthz'),
  news: (locale: string) =>
    req<NewsResponse>(`/api/v1/news?locale=${encodeURIComponent(locale)}`, { cache: 'no-store' }),
  // cookie: forwarded from the server component so per-member 可視權 (locked
  // flags) reflect the caller, not an anonymous request.
  columns: (locale: string, assetClass?: AssetClass, cookie?: string) =>
    req<{ items: ColumnCard[] | null }>(
      `/api/v1/columns?locale=${encodeURIComponent(locale)}${assetClass ? `&asset_class=${assetClass}` : ''}`,
      { cache: 'no-store', headers: cookie ? { cookie } : undefined },
    ),
  // cookie: forward the caller's auth cookie from a server component so the
  // backend can meter the read against the user's monthly column allowance.
  column: (slug: string, locale: string, cookie?: string) =>
    req<ColumnDetail>(`/api/v1/columns/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`, {
      cache: 'no-store',
      headers: cookie ? { cookie } : undefined,
    }),
  quote: (symbol: string, assetClass: AssetClass) =>
    req<Quote>(`/api/v1/instruments/${symbol}/quote?asset_class=${assetClass}`),
  candles: (symbol: string, assetClass: AssetClass, days = 180, interval = '1d') =>
    req<{ symbol: string; candles: Candle[] }>(
      `/api/v1/instruments/${symbol}/candles?asset_class=${assetClass}&days=${days}&interval=${interval}`,
      { cache: 'no-store' },
    ),
  detail: (symbol: string, assetClass: AssetClass, name = '') =>
    req<StockDetail>(
      `/api/v1/instruments/${symbol}/detail?asset_class=${assetClass}${name ? `&name=${encodeURIComponent(name)}` : ''}`,
      { cache: 'no-store' },
    ),
  // Separate from detail() so the chart never blocks on AI generation (~2s cold).
  aiNote: (symbol: string, assetClass: AssetClass, name = '') =>
    req<{ ai: string }>(
      `/api/v1/instruments/${symbol}/ainote?asset_class=${assetClass}${name ? `&name=${encodeURIComponent(name)}` : ''}`,
      { cache: 'no-store' },
    ),
  marketQuotes: () => req<MarketQuotesResponse>('/api/v1/market/quotes', { cache: 'no-store' }),

  // Auth + subscription
  register: (body: { email: string; password: string; display_name?: string; locale?: string }) =>
    req<AuthResp>('/api/v1/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    req<AuthResp>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => req<void>('/api/v1/auth/logout', { method: 'POST' }),
  // me/usage are read by several components per page (AppGate, banners, admin
  // gate). Dedupe them behind a short client cache so a navigation costs one
  // round trip, not three. invalidateProfile() clears it after a state change
  // (login, quota spend, admin grant).
  notifyPrefs: () =>
    req<{ news: boolean; columns: boolean; analysis: boolean; email: boolean; email_available?: boolean }>(
      '/api/v1/me/notify',
      { cache: 'no-store' },
    ),
  setNotifyPrefs: (p: { news: boolean; columns: boolean; analysis: boolean; email: boolean }) =>
    req<{ news: boolean; columns: boolean; analysis: boolean; email: boolean; email_available?: boolean }>(
      '/api/v1/me/notify',
      { method: 'PUT', body: JSON.stringify(p) },
    ),
  me: () => cached('me', () => req<AuthUser>('/api/v1/me', { cache: 'no-store' })),
  usage: () => cached('usage', () => req<Usage>('/api/v1/me/usage', { cache: 'no-store' })),
  changePassword: (currentPassword: string, newPassword: string) =>
    req<void>('/api/v1/me/password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  // Web Push
  pushVapid: () => req<{ public_key: string }>('/api/v1/push/vapid', { cache: 'no-store' }),
  pushSubscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    req<void>('/api/v1/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  pushUnsubscribe: (endpoint?: string) =>
    req<void>('/api/v1/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint: endpoint ?? '' }) }),

  // Admin (role admin/teacher)
  adminAnalytics: () =>
    req<{
      users_total: number;
      new_users: { today: number; week: number; month: number };
      active_users: { today: number; week: number; month: number };
      ai_calls: { today: number; week: number; month: number };
      column_reads: { today: number; week: number; month: number };
      published_columns: number;
      push_subscribers: number;
      paid_members: number;
      daily: { date: string; logins: number; ai_calls: number }[];
    }>('/api/admin/analytics', { cache: 'no-store' }),
  adminPublishers: () =>
    req<{
      publishers: {
        user_id: string;
        email: string;
        name: string;
        asset_class: string;
        keys: { id: string; key_prefix: string; active: boolean; created_at: string }[];
      }[];
    }>('/api/admin/publishers', { cache: 'no-store' }),
  adminGenPublisherKey: (user_id: string, asset_class: string, name: string) =>
    req<{ key: string; key_prefix: string; asset_class: string; name: string }>(
      '/api/admin/publishers/keys',
      { method: 'POST', body: JSON.stringify({ user_id, asset_class, name }) },
    ),
  adminRevokePublisherKey: (id: string) =>
    req<{ revoked: boolean }>(`/api/admin/publishers/keys/${id}`, { method: 'DELETE' }),
  adminAIUsage: () =>
    req<{
      balance_usd: number;
      balance_set_at: string;
      spent_since_set: number;
      spend_source: string;
      remaining_usd: number;
      cost_today: number;
      cost_7d: number;
      cost_30d: number;
      cost_all: number;
      calls_today: number;
      calls_30d: number;
      by_model: { model: string; cost_usd: number; calls: number }[];
      analysis_avg_cost: number;
      analysis_est: number;
      analysis_from_data: boolean;
      chat_avg_cost: number;
      chat_est: number;
      chat_from_data: boolean;
    }>('/api/admin/ai-usage', { cache: 'no-store' }),
  adminSetCredit: (balance_usd: number) =>
    req<{ balance_usd: number }>('/api/admin/ai-credit', {
      method: 'POST',
      body: JSON.stringify({ balance_usd }),
    }),
  adminUsers: (params?: { q?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{
      items: {
        id: string;
        email: string;
        display_name: string;
        role: string;
        status: string;
        plan: string;
        ai_daily: number | null;
        column_categories: string[] | null;
        plan_end: string | null;
        ent_expires: string | null;
        created_at: string;
      }[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/admin/users/list${suffix}`, { cache: 'no-store' });
  },
  adminGetUser: (email: string) =>
    req<{
      id: string;
      email: string;
      display_name: string;
      role: string;
      plan: string;
      ai_daily: number | null;
      column_categories: string[] | null;
    }>(`/api/admin/users?email=${encodeURIComponent(email)}`, { cache: 'no-store' }),
  adminSetEntitlements: (
    userId: string,
    ai_daily: number | null,
    column_categories: string[] | null,
    days = 0, // validity in days; 0 = no expiry
  ) =>
    req<{ user_id: string }>(`/api/admin/users/${userId}/entitlements`, {
      method: 'POST',
      body: JSON.stringify({ ai_daily, column_categories, days }),
    }),
  adminSetPlan: (userId: string, plan_id: string, days: number) =>
    req<{ user_id: string; plan: string; current_period_end: string }>(
      `/api/admin/users/${userId}/plan`,
      { method: 'POST', body: JSON.stringify({ plan_id, days }) },
    ),
  adminSetUserStatus: (userId: string, banned: boolean) =>
    req<{ user_id: string; status: string }>(`/api/admin/users/${userId}/status`, {
      method: 'POST',
      body: JSON.stringify({ banned }),
    }),
  adminDeleteUser: (userId: string) =>
    req<{ deleted: boolean }>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
  adminResetLink: (userId: string) =>
    req<{ url: string; expires_at: string }>(`/api/admin/users/${userId}/reset-link`, {
      method: 'POST',
    }),
  validateReset: (token: string) =>
    req<{ valid: boolean; email?: string }>(
      `/api/v1/auth/reset-password?token=${encodeURIComponent(token)}`,
      { cache: 'no-store' },
    ),
  resetPassword: (token: string, password: string) =>
    req<{ ok: boolean }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  adminColumns: () =>
    req<{
      items: {
        id: string;
        slug: string;
        title: string;
        status: string;
        source: string;
        author_name: string;
        asset_class: string;
        published_at: string | null;
      }[];
    }>('/api/admin/columns', { cache: 'no-store' }),
  adminPublishColumn: (id: string) =>
    req<void>(`/api/admin/columns/${id}/publish`, { method: 'POST' }),
  adminArchiveColumn: (id: string) =>
    req<void>(`/api/admin/columns/${id}/archive`, { method: 'POST' }),
  adminDeleteColumn: (id: string) =>
    req<void>(`/api/admin/columns/${id}`, { method: 'DELETE' }),
  adminTestPush: (email?: string) =>
    req<{
      target: string;
      subscriptions: number;
      results: { host: string; status: number; error?: string }[];
    }>('/api/admin/push/test', { method: 'POST', body: JSON.stringify({ email: email ?? '' }) }),
  adminTestEmail: (email?: string) =>
    req<{ ok: boolean; to: string; elapsed_ms?: number; error?: string; fanout_enabled?: boolean }>(
      '/api/admin/email/test',
      { method: 'POST', body: JSON.stringify({ email: email ?? '' }) },
    ),
  adminGetColumn: (id: string) =>
    req<{ id: string; slug: string; status: string; title: string; subtitle: string; body_md: string }>(
      `/api/admin/columns/${id}`,
      { cache: 'no-store' },
    ),
  adminUpdateColumn: (id: string, body: { title: string; subtitle: string; body_md: string }) =>
    req<void>(`/api/admin/columns/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  adminStevenSetup: (body: {
    direction: string;
    entry: number[];
    tp: number[];
    sl: number;
    notes: string[];
    commentary: string;
    title?: string;
    image_url?: string;
  }) => req<{ slug: string; title: string }>('/api/admin/steven/setup', { method: 'POST', body: JSON.stringify(body) }),
  // Multipart image upload (admin only). Returns a same-origin /api/uploads URL.
  // Note: no content-type header — the browser sets the multipart boundary.
  adminUpload: async (file: File) => {
    // Convert HEIC / shrink large phone photos to a web-safe JPEG first.
    const normalized = await normalizeImage(file);
    const fd = new FormData();
    fd.append('file', normalized);
    const res = await fetch(`${BASE}/api/admin/upload`, { method: 'POST', body: fd, credentials: 'include' });
    if (!res.ok) {
      let code = res.statusText;
      try {
        const j = await res.json();
        if (j && typeof j.message === 'string') code = j.message;
      } catch {
        /* non-JSON */
      }
      throw new ApiError(res.status, code);
    }
    return res.json() as Promise<{ url: string }>;
  },
  agentChat: (locale: string, messages: AgentMessage[]) =>
    req<AgentResult>(`/api/v1/agent/chat?locale=${encodeURIComponent(locale)}`, {
      method: 'POST',
      body: JSON.stringify({ messages }),
    }),
  analyze: (symbol: string, body: { asset_class: AssetClass; horizon: Horizon; name?: string; last_price?: number }) =>
    req<AnalysisResp>(`/api/v1/instruments/${symbol}/analyze`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
